import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const DebugContext = createContext();

export const DebugProvider = ({ children }) => {
  // Check if we're in development environment
  const isDevelopment = process.env.NODE_ENV === 'development';
  
  // Initialize state from localStorage or default based on environment
  const [isDebugVisible, setIsDebugVisible] = useState(() => {
    // Always false in production, unless manually enabled
    if (!isDevelopment && !process.env.REACT_APP_DEBUG_OVERRIDE) {
      return false;
    }
    const saved = localStorage.getItem('debugMode');
    return saved ? JSON.parse(saved) : false;
  });

  // Toggle debug mode
  const toggleDebugMode = useCallback(() => {
    // Prevent toggling in production unless override is enabled
    if (!isDevelopment && !process.env.REACT_APP_DEBUG_OVERRIDE) {
      console.warn('Debug mode is disabled in production');
      return;
    }
    
    setIsDebugVisible(prev => {
      const newValue = !prev;
      localStorage.setItem('debugMode', JSON.stringify(newValue));
      return newValue;
    });
  }, [isDevelopment]);

  // Show debug menu
  const showDebugMode = () => {
    if (!isDevelopment && !process.env.REACT_APP_DEBUG_OVERRIDE) {
      console.warn('Debug mode is disabled in production');
      return;
    }
    setIsDebugVisible(true);
    localStorage.setItem('debugMode', JSON.stringify(true));
  };

  // Hide debug menu
  const hideDebugMode = () => {
    setIsDebugVisible(false);
    localStorage.setItem('debugMode', JSON.stringify(false));
  };

  // Keyboard shortcut listener
  useEffect(() => {
    const handleKeyDown = (event) => {
      // Ctrl+Shift+D (or Cmd+Shift+D on Mac)
      if ((event.ctrlKey || event.metaKey) && event.shiftKey && event.key === 'D') {
        event.preventDefault();
        console.log('🐛 Debug shortcut pressed!');
        toggleDebugMode();
      }
    };

    console.log('🔧 Debug context: Adding keyboard listener');
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      console.log('🔧 Debug context: Removing keyboard listener');
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [toggleDebugMode]);

  // Check if debug is allowed in current environment
  const isDebugAllowed = isDevelopment || process.env.REACT_APP_DEBUG_OVERRIDE;

  const value = {
    isDebugVisible,
    isDebugAllowed,
    isDevelopment,
    toggleDebugMode,
    showDebugMode,
    hideDebugMode,
  };

  return (
    <DebugContext.Provider value={value}>
      {children}
    </DebugContext.Provider>
  );
};

export const useDebug = () => {
  const context = useContext(DebugContext);
  if (context === undefined) {
    throw new Error('useDebug must be used within a DebugProvider');
  }
  return context;
};
